import { useState } from "react";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";
import { api, useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { PasswordInput } from "./PasswordInput";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const inputCls = "h-11 bg-transparent border-white/10 text-white focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPass !== confirm) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPass });
      toast.success("Password aggiornata con successo");
      setUser({ ...user, must_change_password: false });
      setCurrent(""); setNewPass(""); setConfirm("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl">Cambia password</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Imposta una nuova password per il tuo account ({user?.email}).
          </DialogDescription>
        </DialogHeader>
        {user?.must_change_password && (
          <div data-testid="default-password-warning" className="flex items-start gap-2.5 text-sm text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-md px-4 py-3">
            <Warning size={18} weight="duotone" className="shrink-0 mt-0.5" />
            <span>Stai usando la password predefinita. Cambiala ora per proteggere l'account.</span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Password attuale</Label>
            <PasswordInput required data-testid="current-password-input" value={current}
              onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nuova password</Label>
            <PasswordInput required minLength={6} data-testid="new-password-input" value={newPass}
              onChange={(e) => setNewPass(e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Conferma nuova password</Label>
            <PasswordInput required minLength={6} data-testid="confirm-password-input" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
          </div>
          <DialogFooter className="pt-2">
            <Button type="submit" disabled={saving} data-testid="change-password-submit-button"
              className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {saving ? "Salvataggio..." : "Aggiorna password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
